import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import type { AppointmentWithDetails, Veterinarian } from "@/hooks/useAppointments";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";
import { getEventPetsLabel, getEventTypeClasses, hexToRgba } from "./views/calendarEventUtils";
import { getInitials } from "@/lib/utils/string-utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useDraggable } from "@dnd-kit/core";
import { Badge } from "../ui/badge";
import { renderIconBySlug } from "@/lib/event-icons";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";
import { buildAppointmentSummaryItems } from "@/lib/utils/appointment-summary-items";
import { AppointmentCardSummaryLines } from "./views/AppointmentCardSummaryLines";
import { ClinicTag } from "@/components/clinics/ClinicTag";
import { AppointmentDriverBadge } from "@/components/calendar/AppointmentDriverBadge";
import { PetAlertBadges, isPetAggressive } from "@/components/pets/PetAlertBadges";
import type { PetConditionEntry } from "@/components/pets/PetAlertBadges";

interface AppointmentKanbanCardProps {
  appointment: AppointmentWithDetails;
  statuses?: AppointmentStatus[];
  professionals?: Veterinarian[];
  onClick?: () => void;
  showAppointmentItemsOnCards?: boolean;
}

// Helper para converter AppointmentWithDetails para formato similar ao CalendarEvent
function appointmentToEvent(appointment: AppointmentWithDetails) {
  return {
    id: appointment.id,
    title: "",
    start: new Date(appointment.starts_at),
    end: new Date(appointment.ends_at),
    type: appointment.type,
    status: appointment.status,
    vet_user_id: appointment.vet_user_id,
    pet: appointment.pet && typeof appointment.pet.id === "string" ? { id: appointment.pet.id, name: typeof appointment.pet.name === "string" ? appointment.pet.name : null } : null,
    pets: (appointment.pets ?? []).filter((pet) => typeof pet.id === "string").map((pet) => ({ id: String(pet.id), name: typeof pet.name === "string" ? pet.name : null })),
    owner: { name: typeof appointment.owner?.name === "string" ? appointment.owner.name : null },
    vet: { full_name: typeof appointment.vet?.full_name === "string" ? appointment.vet.full_name : null },
    notes: appointment.notes,
    is_taxi_dog: appointment.is_taxi_dog,
    taxi_dog_type: appointment.taxi_dog_type,
  };
}


export function AppointmentKanbanCard({
  appointment,
  statuses: _statuses,
  professionals,
  onClick,
  showAppointmentItemsOnCards = false,
}: AppointmentKanbanCardProps) {
  const { t } = useTranslation("calendar");
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: appointment.id,
  });

  const professionalsById = useMemo(() => {
    return new Map<string, Veterinarian>((professionals ?? []).map((p) => [p.id, p]));
  }, [professionals]);

  const vetUserId = appointment.vet_user_id ?? null;
  const resolvedProfessional = vetUserId ? professionalsById.get(vetUserId) : undefined;
  const resolvedVetName =
    (appointment.vet as { full_name?: string | null } | null | undefined)?.full_name ??
    resolvedProfessional?.full_name ??
    null;
  // Avatar só vem do join, não do Veterinarian type
  const resolvedVetAvatar =
    (appointment.vet as { avatar_url?: string | null } | null | undefined)?.avatar_url ?? null;

  const event = appointmentToEvent(appointment);
  const ownerName = typeof appointment.owner?.name === "string" ? appointment.owner.name : "";
  const startsAt = new Date(appointment.starts_at);

  // Collect pet conditions from all pets associated to this appointment
  const allPetConditions = useMemo<PetConditionEntry[]>(() => {
    const petsArray = (appointment.pets ?? (appointment.pet ? [appointment.pet] : [])) as Array<{
      pet_conditions?: PetConditionEntry[] | null;
    }>;
    return petsArray.flatMap((p) => p?.pet_conditions ?? []);
  }, [appointment.pets, appointment.pet]);

  const petIsAggressive = isPetAggressive(allPetConditions);
  const timeLabel = format(startsAt, "HH:mm", { locale: ptBR });
  const financialStatus = deriveFinancialStatusWithLegacyFallback(appointment.appointment_financial, {
    status: appointment.status,
    grooming_status: appointment.grooming_status,
  }, appointment.linked_sale);
  const showPaidBadge = financialStatus === "pago";
  const summaryItems = useMemo(() => buildAppointmentSummaryItems(appointment, t), [appointment, t]);

  return (
    <div
      ref={setNodeRef}
      className={cn("transition-opacity", isDragging && "opacity-50")}
      {...listeners}
      {...attributes}
    >
      <Card
        className={cn(
          "p-3 cursor-move hover:shadow-md transition-shadow border-l-4 my-3 mx-1",
          getEventTypeClasses(appointment.clinic_appointment_type?.behavior ?? appointment.type),
        )}
        style={{
          borderLeftColor: appointment.clinic_appointment_type?.color ?? "#6366f1",
          ...(appointment.clinic_appointment_type?.color
            ? { background: `linear-gradient(${hexToRgba(appointment.clinic_appointment_type.color, 0.08)}, ${hexToRgba(appointment.clinic_appointment_type.color, 0.08)}), #ffffff` }
            : {}),
        }}
        onClick={onClick}
      >
        <div className="space-y-1">
          {/* Horário no topo */}
          <div className="flex items-center gap-1.5 border border-color-light text-color-dark px-1.5 py-0.5 rounded-md w-fit">
            <Clock className="h-3 w-3" />
            <span className="text-xs font-semibold">{timeLabel}</span>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1 flex-wrap">
              <p className="text-sm font-semibold leading-tight truncate">
                {getEventPetsLabel(event)}
              </p>
              {showPaidBadge ? (
                <Badge className="h-4 px-1 text-[9px] font-medium bg-emerald-700 text-white border-transparent">
                  {t("financialSettled")}
                </Badge>
              ) : null}
              <AppointmentDriverBadge
                isTaxiDog={appointment.is_taxi_dog}
                driverType={appointment.taxi_dog_type ?? null}
                iconClassName="h-3 w-3"
              />
              <ClinicTag clinicId={appointment.clinic_id} className="h-4 px-1 text-[9px]" />
            </div>
            {ownerName ? (
              <p className="mt-0.5 text-xs leading-tight opacity-80 truncate">
                {ownerName}
              </p>
            ) : null}
            {petIsAggressive ? (
              <PetAlertBadges
                conditions={allPetConditions}
                className="mt-1"
              />
            ) : null}
            {showAppointmentItemsOnCards ? (
              <AppointmentCardSummaryLines items={summaryItems} variant="kanban" />
            ) : null}

          </div>

          {/* Rodapé com avatar e ícone */}
          <div className="mt-auto flex items-center pt-1">
            {resolvedVetName ? (
              <Avatar className="h-6 w-6">
                {resolvedVetAvatar ? (
                  <AvatarImage
                    src={resolvedVetAvatar}
                    alt={resolvedVetName ?? t("professionalAlt")}
                  />
                ) : null}
                <AvatarFallback className="text-[9px] bg-white border border-slate-200">{getInitials(resolvedVetName)}</AvatarFallback>
              </Avatar>
            ) : (
              <span className="h-6 w-6" />
            )}

            <div
              className="h-5 w-5 rounded flex items-center justify-center shrink-0 ml-auto"
              style={{ backgroundColor: appointment.clinic_appointment_type?.color ?? "#6366f1" }}
            >
              {renderIconBySlug(appointment.clinic_appointment_type?.icon ?? "clipboard-plus", { tone: "white", sizeClass: "h-3.5 w-3.5" })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
